/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnChanges } from '@angular/core';
import { FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { RpcSelectControltOptions } from '@core-controls/components/rpc-select/models/rpc-select-controlt-options';
import { RpcSelectOption } from '@core-controls/components/rpc-select/models/rpc-select-option';
import { PhoneInfo } from '@core-controls/models/phone-info';
import { SimpleChanges } from '@core-models/utilities/generic-simple-changes';
import { Phone } from '../../interfaces/customer-info/phone';
import { PhoneTypes } from '../../interfaces/enums/phone-types.enum';
import { PhoneService } from '@core-services/phone/phone.service';
import { RpcButtonComponent } from '../../../../../core-modules/controls/components/rpc-button/rpc-button.component';
import { RpcSelectComponent } from '../../../../../core-modules/controls/components/rpc-select/rpc-select.component';
import { PhoneFormInputComponent } from '../../../../../core-modules/controls/components/phone-form-input/phone-form-input.component';
import { RpcSlideToggleComponent } from '../../../../../core-modules/controls/components/rpc-slide-toggle/rpc-slide-toggle.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'phones-edit-form',
    templateUrl: './phones-edit-form.component.html',
    styleUrls: ['../profile-info-base/profile-info-base.component.scss', './phones-edit-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RpcButtonComponent, RpcSelectComponent, PhoneFormInputComponent, RpcSlideToggleComponent, TranslateModule]
})

export class PhonesEditFormComponent implements OnChanges {
    @Input() public parentGroup: UntypedFormGroup;
    @Input() public controlName: string;
    @Input() public phones: Phone[];

    public phoneControls: { id: number, isAlertReceiver: boolean, selectOptions: RpcSelectControltOptions, phoneInfo: PhoneInfo, form: FormGroup }[];
    public readonly countriesPhoneInfo = inject(PhoneService).countryPhoneInfo;

    private tempPhoneId = -1;

    private readonly phoneSelectOptionValues: RpcSelectOption[] = [
        { text: 'PHONES_EDIT_FORM.TITLES.OFFICE', value: PhoneTypes.office },
        { text: 'PHONES_EDIT_FORM.TITLES.MOBILE', value: PhoneTypes.mobile },
        { text: 'PHONES_EDIT_FORM.TITLES.HOME', value: PhoneTypes.home },
        { text: 'PHONES_EDIT_FORM.TITLES.OTHER', value: PhoneTypes.other }
    ];

    private phoneArray: UntypedFormArray;

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private readonly changeDetectorRef: ChangeDetectorRef
    ) { }

    public ngOnChanges(changes: SimpleChanges<PhonesEditFormComponent>): void {
        const { controlName, parentGroup, phones } = changes;

        if ([controlName?.currentValue, parentGroup?.currentValue, phones?.currentValue].every(x => x != null)) {
            this.buildPhoneControls(controlName.currentValue, parentGroup.currentValue, phones.currentValue);
        }
    }

    public onAddPhone(): void {
        this.createPhoneControl(this.tempPhoneId);
        this.tempPhoneId -= 1;
        this.changeDetectorRef.detectChanges();
    }

    public onRemovePhone(i: number): void {
        this.phoneControls.splice(i, 1);
        this.phoneArray.removeAt(i);
    }

    // eslint-disable-next-line max-len
    public onAlertReceiverChanged(currentPhoneControl: { id: number, isAlertReceiver: boolean, selectOptions: RpcSelectControltOptions, phoneInfo: PhoneInfo, form: FormGroup }): void {
        if (!currentPhoneControl?.phoneInfo) {
            return;
        }

        const isAlertReceiverValue = !currentPhoneControl.phoneInfo.isAlertReceiver;

        currentPhoneControl.phoneInfo.isAlertReceiver = isAlertReceiverValue;
        currentPhoneControl.form.get('isAlertReceiver')?.setValue(isAlertReceiverValue);

        if (isAlertReceiverValue) {
            this.phoneControls.forEach(phoneControl => {
                if (phoneControl.id !== currentPhoneControl.id) {
                    phoneControl.phoneInfo.isAlertReceiver = false;
                    phoneControl.form.get('isAlertReceiver')?.setValue(false);
                }
            });
        }

        this.changeDetectorRef.detectChanges();
    }

    private buildPhoneControls(controlName: string, parentGroup: UntypedFormGroup, phones: Phone[]): void {
        this.phoneControls = [];

        this.phoneArray = this.formBuilder.array([]);
        phones.forEach(phone => this.createPhoneControl(phone.id, phone));

        parentGroup.addControl(controlName, this.phoneArray);
    }

    private createPhoneControl(id: number, phone: Phone = null): void {
        const phoneValidatorsMap = [{
            message: 'PHONES_EDIT_FORM.ERRORS.CLIENT.PHONE_TYPE_REQUIRED',
            showError: (control: UntypedFormControl) => control.hasError('required'),
            validator: Validators.required
        }];

        const isAlertReceiver = phone?.isAlertReceiver || false;

        const currentGroup = this.formBuilder.group({
            id,
            isAlertReceiver: [isAlertReceiver]
        });

        const phoneInfo: PhoneInfo = phone == null
            ? { id, phoneNumber: '', countryCode: null, isAlertReceiver }
            : { ...phone.number, id, isAlertReceiver };

        this.phoneControls.push({
            id,
            isAlertReceiver,
            form: currentGroup,
            selectOptions: new RpcSelectControltOptions(currentGroup, 'type', this.phoneSelectOptionValues, phone?.type, phoneValidatorsMap),
            phoneInfo
        });

        this.phoneArray.push(currentGroup);
    }
}